import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export interface CircumferentialSlotDiagramCopy {
  sectionTitle: string;
  sectionLead: string;
}

export interface CircumferentialSlotPageData {
  content: SubPageContent;
  diagramCopy: CircumferentialSlotDiagramCopy;
}

export const COPY: Record<Lang, CircumferentialSlotPageData> = {
  en: {
    diagramCopy: {
      sectionTitle: "Circumferential Slot Rotary Screening Architecture",
      sectionLead: "Helically wound surface wires welded 360 degrees around longitudinal support rods provide maximum hoop collapse strength for rotary trommels and internal-feed screening drums.",
    },
    content: {
      breadcrumbKey: "CircumferentialSlotCylinders",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Wedge Wire Cylinders",
        standardBadge: "Helical Continuous V-Wire",
        heading: "Circumferential Slot Cylinders",
        lead: "Reinforced cylindrical wedge wire screens with continuous profile wires wound around the circumference. Engineered for rotary trommels, vibrating dewatering screens, and internal-feed drums demanding high hoop strength and smooth axial transport.",
        secondaryCta: {
          label: "View All Cylindrical Screens",
          to: "/CylindricalScreensPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
          alt: "Stainless steel circumferential slot cylinder screen with continuous radial wedge wire for rotary trommels",
        },
        cardNote: "Rotary Drums & Trommels · 360° Wound",
        cardLink: {
          label: "Request a Quote",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Slot Width", value: "0.02 – 5.0 mm" },
          { label: "Orientation", value: "Around Circumference" },
          { label: "Diameter Range", value: "25.4 – 1200 mm" },
          { label: "Standard Alloys", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Radial Strength & Rotary Transport",
        heading: "Continuous Helical Winding for Rotary Dewatering & Trommel Systems",
        paragraphs: [
          "MutualFilter circumferential slot cylinders feature continuous cold-rolled V-wire wound spirally around an internal or external cage of axial support rods. Every intersection is fused by high-precision resistance welding, creating a rigid cylindrical body with outstanding hoop strength capable of resisting intense centrifugal forces and deep slurry bed loads.",
          "In rotary screening applications, solids tumble or slide axially along the length of the cylinder while the liquid discharges radially through the circumferential slots. Because the profile wires encircle the circumference, the tumbling material naturally bridges across adjacent wires, preventing slender particles from wedging end-first into the slot openings.",
          "Circumferential slot screens are configured with the flat working surface facing inwards (Flow-In-To-Out, FITO) for internal-feed trommels with internal screw flights, or facing outwards (Flow-Out-To-In, FOTI) for externally fed dewatering drums and radial cartridge strainers.",
        ],
        keyPoints: [
          "Continuous 360-degree helical profile wire delivers exceptional hoop strength to prevent radial collapse.",
          "Circumferential slot alignment allows tumbling solids to travel axially without wedging end-first into apertures.",
          "Available with internal smooth working surfaces for internal-feed trommel drums or external smooth surfaces.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
          alt: "Circumferential slot cylinder screen showing continuous radial profile wires and axial support rods",
        },
      },
      specTable: {
        heading: "Circumferential Slot Cylinder Technical Data",
        intro: "Custom fabricated in diameters up to 1200 mm with drive tires, reinforcing bands, and flanged connections for rotary drum installations.",
        columns: [
          { key: "parameter", label: "Parameter", align: "left" },
          { key: "specification", label: "Standard Specification", align: "left" },
          { key: "notes", label: "Design Notes", align: "left" },
        ],
        rows: [
          {
            parameter: "Slot Width",
            specification: "0.02 – 5.0 mm",
            notes: "Accurate slot apertures maintained across the full circumference",
          },
          {
            parameter: "Slot Orientation",
            specification: "Around Circumference (Radial)",
            notes: "Continuous helical wire loop wound on axial structural rods",
          },
          {
            parameter: "Diameter Range",
            specification: "25.4 – 1200 mm",
            notes: "From compact line strainers up to large industrial rotary trommel cylinders",
          },
          {
            parameter: "Standard Alloys",
            specification: "AISI 304, AISI 316, 316L",
            notes: "Austenitic stainless steels offering corrosion and abrasion resistance",
          },
          {
            parameter: "Flow Direction",
            specification: "FITO (Internal Feed) or FOTI (External Feed)",
            notes: "Internal smooth surface for rotating trommel barrels, external for strainers",
          },
        ],
        caption: "Specifications based on standard resistance-welded wedge wire manufacturing limits.",
      },
      features: {
        heading: "Key Engineering Capabilities",
        intro: "Constructed to endure continuous dynamic rotation, heavy solids loads, and abrasive slurry conditions.",
        items: [
          {
            title: "Maximum Hoop Strength",
            text: "Helical continuous wire winding provides superior resistance against external pressure and centrifugal forces.",
          },
          {
            title: "Smooth Axial Solids Travel",
            text: "Solids move freely along the cylinder length without catching on transverse cross-wires.",
          },
          {
            title: "Rigid Axial Support Grid",
            text: "Heavy-gauge axial support rods prevent cylinder sagging over long unsupported spans.",
          },
          {
            title: "Custom Structural Framing",
            text: "Equipped with drive rings, tire rings, bolt-hole flanges, or internal flight brackets as needed.",
          },
        ],
      },
      related: {
        heading: "Related Wedge Wire Cylinders",
        intro: "Compare circumferential slot architecture with axial slot cylinders or return to the parent category.",
        cards: [
          {
            title: "Axial Slot Cylinders",
            text: "Continuous slots run parallel to the cylinder axis, providing clean linear tracks for mechanical scraper systems, brush cleaning, and radial flow filtration.",
            to: "/AxialSlotCylindersPage",
            badge: "Parallel to Axis",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
              alt: "Axial slot wedge wire cylindrical screen with longitudinal profile wire arrangement",
            },
            linkLabel: "View Axial Slot Cylinders",
          },
          {
            title: "Cylindrical Screens Overview",
            text: "Explore the full spectrum of precision resistance-welded cylindrical wedge wire screens, manufacturing limits, and standard alloy options.",
            to: "/CylindricalScreensPage",
            badge: "Parent Category",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Industrial cylindrical stainless steel wedge wire filter screens and support cores",
            },
            linkLabel: "View Cylindrical Screens",
          },
        ],
      },
      cta: {
        id: "circumferential-slot-quote",
        eyebrow: "Engineering Inquiry",
        heading: "Request Circumferential Slot Cylinder Quotation",
        lead: "Provide your drum diameter, total length, slot width, and installation details for an engineering proposal.",
        secondaryLabel: "Return to Cylindrical Screens",
        secondaryTo: "/CylindricalScreensPage",
        note: "Material test reports and positive material identification available when specified in the order.",
      },
    },
  },
  zh: {
    diagramCopy: {
      sectionTitle: "环向缝隙转鼓筛分结构原理",
      sectionLead: "型材丝沿圆周 360 度连续螺旋缠绕并熔焊于轴向纵向骨架支撑筋上，赋予旋转转鼓与脱水圆筒优越的环向抗压溃与抗离心力变形能力。",
    },
    content: {
      breadcrumbKey: "CircumferentialSlotCylinders",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "圆柱楔形丝筛网",
        standardBadge: "环向连续螺旋缠绕 V 型丝",
        heading: "环向缝隙圆筒筛",
        lead: "采用型材丝沿圆周方向连续螺旋缠绕熔焊的高强度圆筒筛网。专为转鼓筛、滚筒筛、振动脱水机及内进料旋转过滤设备设计，具备极佳的环向抗压溃强度及平稳的物料轴向推进输送性能。",
        secondaryCta: {
          label: "查看圆柱筛筒总览",
          to: "/CylindricalScreensPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
          alt: "全周向环绕缝隙不锈钢圆筒筛网与转鼓分选结构",
        },
        cardNote: "转鼓筛分 · 360° 连续缠绕",
        cardLink: {
          label: "获取工程报价",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "缝隙宽度", value: "0.02 – 5.0 mm" },
          { label: "缝隙走向", value: "圆周环绕" },
          { label: "直径范围", value: "25.4 – 1200 mm" },
          { label: "标准材质", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "环向抗压溃与转鼓轴向物料输送",
        heading: "专为旋转转鼓筛与脱水离心系统定制的连续螺旋缠绕结构",
        paragraphs: [
          "MutualFilter 环向缝隙圆筒筛采用冷轧不锈钢 V 型丝在纵向轴向支撑筋构架上沿圆周方向进行 360 度连续螺旋缠绕，每个交点经全电阻熔焊融为一体。该结构赋予圆筒出色的环向抗压溃刚度，能够承受高浓度矿浆物料重力负荷与转鼓高速旋转下的剧烈离心力。",
          "在转鼓分选与脱水过滤过程中，固体物料沿圆筒轴向向前翻滚推进，液体介质则通过圆周缝隙向外高速排出。由于筛条沿圆周布置，颗粒物料跨立于相邻缝隙之上，有效防止针状、片状颗粒端部顺缝直插导致的堵塞卡死。",
          "环向圆筒筛可灵活配置为内进料平滑工作面（由内向外，FITO，适用于内进料滚筒筛并可焊接内部螺旋导料叶片）或外表面平滑工作面（由外向内，FOTI，适用于外部喷淋过滤与径向外滤式滤筒）。",
        ],
        keyPoints: [
          "360 度连续螺旋缠绕焊接轮廓丝，赋予圆筒超高环向抗压溃与抗离心力变形能力。",
          "环向缝隙排列使翻滚前进的物料沿轴向顺畅推移，杜绝细长颗粒垂直卡塞缝隙。",
          "支持内表面平整光滑（内进料旋转转鼓）或外表面光滑两种结构形式，适应各种流体进出模式。",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
          alt: "展现环向连续异型丝与轴向支撑骨架的圆筒筛网细节",
        },
      },
      specTable: {
        heading: "环向缝隙圆筒筛技术规格表",
        intro: "最大制造直径可达 1200 mm，可配置驱动轮缘、中间加强圈、导料螺旋叶片及装配法兰。",
        columns: [
          { key: "parameter", label: "技术参数", align: "left" },
          { key: "specification", label: "标准制造范围", align: "left" },
          { key: "notes", label: "设计说明", align: "left" },
        ],
        rows: [
          {
            parameter: "缝隙宽度",
            specification: "0.02 – 5.0 mm",
            notes: "全圆周 360 度范围内缝隙公差严格均匀受控",
          },
          {
            parameter: "缝隙走向",
            specification: "圆周环绕（径向）",
            notes: "V 型截面丝连续螺旋缠绕在轴向纵梁支撑筋上",
          },
          {
            parameter: "直径范围",
            specification: "25.4 – 1200 mm",
            notes: "从小型管路滤芯到大型工业重型滚筒筛筒体",
          },
          {
            parameter: "标准材质",
            specification: "AISI 304, AISI 316, 316L",
            notes: "奥氏体不锈钢材质，具备优异的抗腐蚀与耐磨性能",
          },
          {
            parameter: "介质流向",
            specification: "FITO（内进料）或 FOTI（外进料）",
            notes: "内工作面适合转鼓内部翻滚，外工作面适合外滤脱水",
          },
        ],
        caption: "数据代表环向全电阻熔焊楔形丝标准工艺公差范围。",
      },
      features: {
        heading: "制造工程与结构优势",
        intro: "专为承受严苛的动态旋转工况、高浓度料浆冲击以及耐磨磨损环境打造。",
        items: [
          {
            title: "超高环向抗压溃强度",
            text: "环周连续丝圈提供天然的几何拱形刚性，强力抵御外界挤压力与离心载荷。",
          },
          {
            title: "物料轴向顺畅推移",
            text: "翻滚物料沿轴向无阻力滑动，彻底消除横向交叉构件引起的挂料滞留。",
          },
          {
            title: "刚性纵梁骨架支撑",
            text: "全熔焊重型轴向加强筋，有效防止长跨度转鼓筛筒在旋转时产生下垂变形。",
          },
          {
            title: "完善的结构安装配件",
            text: "支持焊接托轮滚带、齿轮驱动环、加固法兰及内部螺旋分料叶片。",
          },
        ],
      },
      related: {
        heading: "相关圆柱楔形丝产品",
        intro: "对比环向缝隙与轴向缝隙的工况适用差异，或返回圆柱形滤筒产品大类。",
        cards: [
          {
            title: "轴向缝隙滤筒",
            text: "连续缝隙沿圆柱轴向平行延伸，为自清洗刮刀、内/外部机械刮渣与径向流体分离提供平滑的线性导向表面。",
            to: "/AxialSlotCylindersPage",
            badge: "轴向平行排列",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
              alt: "轴向缝隙楔形丝圆柱筛筒与平行排列结构",
            },
            linkLabel: "查看轴向滤筒详情",
          },
          {
            title: "圆柱形楔形丝筛网总览",
            text: "全面了解全电阻熔焊圆柱楔形丝滤筒的整体技术规格、制造范围与标准合金配置。",
            to: "/CylindricalScreensPage",
            badge: "上级父分类",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "工业级高精度不锈钢圆柱楔形丝滤筒与内部支撑骨架",
            },
            linkLabel: "返回圆柱筛筒总览",
          },
        ],
      },
      cta: {
        id: "circumferential-slot-quote",
        eyebrow: "环向筛筒技术咨询",
        heading: "获取环向缝隙圆筒筛技术方案与报价",
        lead: "请提供您的转鼓外径、有效筛分长度、缝隙精度及安装方式，工程技术团队将为您出具详细图纸及方案。",
        secondaryLabel: "返回圆柱筛网分类",
        secondaryTo: "/CylindricalScreensPage",
        note: "订单明确要求时可按需提供材质检验报告与成分检测光谱分析。",
      },
    },
  },
  es: {
    diagramCopy: {
      sectionTitle: "Arquitectura Rotativa de Ranura Circunferencial",
      sectionLead: "Alambres enrollados en hélice continua soldados 360 grados sobre varillas longitudinales que ofrecen máxima resistencia al colapso radial en tromeles y tambores rotativos.",
    },
    content: {
      breadcrumbKey: "CircumferentialSlotCylinders",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Cilindros de Alambre en Cuña",
        standardBadge: "Alambre en V Helicoidal",
        heading: "Cilindros de Ranura Circunferencial",
        lead: "Cilindros de alambre en cuña con alambres continuos enrollados alrededor de la circunferencia. Diseñados para tromeles rotativos, tambores de deshidratación y cribas de alimentación interna que exigen alta resistencia radial y suave transporte axial.",
        secondaryCta: {
          label: "Ver Todos los Cilindros",
          to: "/CylindricalScreensPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
          alt: "Cilindro de ranura circunferencial de alambre en cuña para tamizado rotativo",
        },
        cardNote: "Tambores Rotativos y Tromeles · 360°",
        cardLink: {
          label: "Solicitar Presupuesto",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Ancho de ranura", value: "0.02 – 5.0 mm" },
          { label: "Orientación", value: "Circunferencial" },
          { label: "Rango de diámetro", value: "25.4 – 1200 mm" },
          { label: "Aleaciones estándar", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Resistencia Radial y Transporte Axial Rotativo",
        heading: "Bobinado Helicoidal Continuo para Tambores Rotativos y Deshidratación",
        paragraphs: [
          "Los cilindros de ranura circunferencial MutualFilter presentan un alambre continuo en V enrollado en espiral sobre varillas de soporte longitudinales. La soldadura por resistencia en cada cruce genera una estructura cilíndrica de gran resistencia radial capaz de soportar cargas centrífugas elevadas y el peso de pulpas densas.",
          "En cribas rotativas, los sólidos ruedan o se desplazan axialmente mientras el líquido descarga radialmente a través de las ranuras. La disposición circunferencial permite que las partículas apoyen sobre varios alambres adyacentes, evitando que sólidos alargados se encajen en las aberturas.",
          "Se configuran con superficie lisa interior (FITO) para tambores con espirales internas de avance, o exterior (FOTI) para tambores de alimentación externa y filtros de cartucho radial.",
        ],
        keyPoints: [
          "El alambre continuo enrollado en 360 grados proporciona una rigidez insuperable frente al colapso radial.",
          "La orientación circunferencial permite que los sólidos avancen longitudinalmente sin atascarse en las ranuras.",
          "Disponible con cara de trabajo interior lisa para tromeles rotativos o cara exterior lisa para filtros de cartucho.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
          alt: "Detalle de cilindro con ranura circunferencial y varillas axiales",
        },
      },
      specTable: {
        heading: "Datos Técnicos de Cilindros Circunferenciales",
        intro: "Fabricación a medida con diámetros hasta 1200 mm, aros de rodadura, bridas de accionamiento y aros de refuerzo.",
        columns: [
          { key: "parameter", label: "Parámetro", align: "left" },
          { key: "specification", label: "Especificación", align: "left" },
          { key: "notes", label: "Notas de Diseño", align: "left" },
        ],
        rows: [
          {
            parameter: "Ancho de ranura",
            specification: "0.02 – 5.0 mm",
            notes: "Ranura uniforme en todo el perímetro y longitud del cilindro",
          },
          {
            parameter: "Orientación",
            specification: "Circunferencial (Radial)",
            notes: "Alambre continuo helicoidal sobre varillas axiales",
          },
          {
            parameter: "Rango de diámetro",
            specification: "25.4 – 1200 mm",
            notes: "Desde pequeños cartuchos tubulares hasta grandes tromeles rotativos",
          },
          {
            parameter: "Aleaciones estándar",
            specification: "AISI 304, AISI 316, 316L",
            notes: "Acero inoxidable para resistencia química y al desgaste por abrasión",
          },
          {
            parameter: "Dirección de flujo",
            specification: "FITO (Alimentación interna) o FOTI (Alimentación externa)",
            notes: "Superficie de trabajo lisa interior o exterior según el equipo",
          },
        ],
        caption: "Valores basados en tolerancias de fabricación de alambre en cuña soldado por resistencia.",
      },
      features: {
        heading: "Ventajas de Ingeniería y Fabricación",
        intro: "Diseñado para resistir rotación dinámica continua y condiciones abrasivas en deshidratación.",
        items: [
          {
            title: "Máxima Resistencia al Colapso",
            text: "El bobinado helicoidal continuo crea un arco rígido que soporta altas presiones radiales.",
          },
          {
            title: "Transporte Axial Fluido",
            text: "Los sólidos ruedan sin resistencia a lo largo del cilindro sin tropezar con alambres transversales.",
          },
          {
            title: "Soporte Longitudinal Robusto",
            text: "Varillas de soporte axiales soldadas que evitan el pandeo en cilindros de gran longitud.",
          },
          {
            title: "Accesorios de Montaje Integrados",
            text: "Posibilidad de soldar aros de rodadura, bridas de fijación y espirales internas de avance.",
          },
        ],
      },
      related: {
        heading: "Cilindros Relacionados",
        intro: "Compare la arquitectura circunferencial con los cilindros de ranura axial.",
        cards: [
          {
            title: "Cilindros de Ranura Axial",
            text: "Las ranuras continuas corren paralelas al eje del cilindro, ofreciendo pistas lineales limpias para raspadores mecánicos y filtración radial de alto caudal.",
            to: "/AxialSlotCylindersPage",
            badge: "Paralela al eje",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
              alt: "Cilindro de alambre en cuña con ranura axial y perfiles longitudinales",
            },
            linkLabel: "Ver Cilindros Axiales",
          },
          {
            title: "Cilindros Filtrantes General",
            text: "Consulte todas las opciones de cilindros de alambre en cuña, límites de fabricación y variantes.",
            to: "/CylindricalScreensPage",
            badge: "Categoría Principal",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Telas filtrantes cilíndricas industriales de alambre en cuña de acero inoxidable",
            },
            linkLabel: "Volver a Cilindros Filtrantes",
          },
        ],
      },
      cta: {
        id: "circumferential-slot-quote",
        eyebrow: "Consulta Técnica",
        heading: "Solicite Cotización para Cilindros Circunferenciales",
        lead: "Indique diámetro del tambor, longitud total, abertura de ranura y detalles de montaje a nuestro equipo.",
        secondaryLabel: "Volver a Cilindros Filtrantes",
        secondaryTo: "/CylindricalScreensPage",
        note: "Certificados de ensayo de materiales e identificación positiva de aleación disponibles bajo pedido.",
      },
    },
  },
  de: {
    diagramCopy: {
      sectionTitle: "Rotationssieb-Prinzip des Umfangsschlitzes",
      sectionLead: "Wendelförmig 360 Grad um axiale Tragstäbe geschweißte Profildrähte sichern höchste radiale Kollapsfestigkeit für Trommelsiebe und Rotationsfilter.",
    },
    content: {
      breadcrumbKey: "CircumferentialSlotCylinders",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Spaltsiebzylinder",
        standardBadge: "Wendelförmiger V-Draht",
        heading: "Umfangsschlitz-Zylinder",
        lead: "Verstärkte Spaltsiebzylinder mit kontinuierlich um den Umfang gewickelten V-Drähten. Konstruiert für Rotationstrommeln, Vibrationsentwässerungssiebe und Innenbeschickungszylinder mit maximaler radialer Festigkeit und axialem Feststofftransport.",
        secondaryCta: {
          label: "Alle Zylinder ansehen",
          to: "/CylindricalScreensPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
          alt: "Spaltsiebzylinder mit Umfangsschlitz für Trommelfiltration und Drehrohre",
        },
        cardNote: "Trommelsiebe & Trommelfilter · 360°",
        cardLink: {
          label: "Angebot anfordern",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Spaltweite", value: "0.02 – 5.0 mm" },
          { label: "Ausrichtung", value: "Umlaufend" },
          { label: "Durchmesserbereich", value: "25.4 – 1200 mm" },
          { label: "Standard-Legierungen", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Radiale Festigkeit & Axialer Materialtransport",
        heading: "Kontinuierliche Drahtwicklung für Trommelfilter und Entwässerung",
        paragraphs: [
          "MutualFilter Umfangsschlitzzylinder wickeln den V-förmigen Spaltsiebdraht kontinuierlich spiralförmig um ein inneres oder äußeres Gitter aus axialen Tragstäben. Die Schweißung an jedem Schnittpunkt erzeugt einen hochfesten Zylinderkörper, der enormen Zentrifugalkräften und dichten Trübelasten standhält.",
          "In Rotationstrommeln wälzen sich die Feststoffe axial entlang des Zylinders, während das Filtrat radial durch die Umfangsschlitze abfließt. Durch die Queranordnung der Schlitze zur Transportrichtung stützen sich Partikel auf benachbarten Drähten ab, was ein Verklemmen schlanker Körner wirksam verhindert.",
          "Erhältlich mit glatter Arbeitsfläche innen (FITO) für Innenbeschickungstrommeln mit Förderschnecken oder außen (FOTI) für Außenbeschickung und Radialfilter.",
        ],
        keyPoints: [
          "Kontinuierlich um 360 Grad gewickelter Profildraht bietet hervorragende Beständigkeit gegen radialen Kollaps.",
          "Umlaufende Schlitze ermöglichen den ungehinderten axialen Transport wälzender Feststoffe ohne Schlitzverstopfung.",
          "Ausführungen mit glatter Innenfläche für Trommelsiebe oder glatter Außenfläche für radiale Filterkerzen.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
          alt: "Detailansicht eines Spaltsiebzylinders mit Umfangsschlitz",
        },
      },
      specTable: {
        heading: "Technische Daten der Umfangsschlitz-Zylinder",
        intro: "Fertigung nach Maß bis 1200 mm Durchmesser mit Laufringen, Verstärkungsbändern und Flanschen.",
        columns: [
          { key: "parameter", label: "Parameter", align: "left" },
          { key: "specification", label: "Spezifikation", align: "left" },
          { key: "notes", label: "Konstruktionshinweise", align: "left" },
        ],
        rows: [
          {
            parameter: "Spaltweite",
            specification: "0.02 – 5.0 mm",
            notes: "Gleichmäßige Spaltweiten über den gesamten Umfang und die Zylinderlänge",
          },
          {
            parameter: "Schlitzausrichtung",
            specification: "Umlaufend (Radial)",
            notes: "Kontinuierlich gewickelter V-Draht auf axialen Tragstäben",
          },
          {
            parameter: "Durchmesserbereich",
            specification: "25.4 – 1200 mm",
            notes: "Von kompakten Siebrohren bis zu großen Industrietrommeln",
          },
          {
            parameter: "Standard-Legierungen",
            specification: "AISI 304, AISI 316, 316L",
            notes: "Edelstahl für Korrosionsbeständigkeit und Verschleißfestigkeit",
          },
          {
            parameter: "Durchströmung",
            specification: "FITO (Innenbeschickung) oder FOTI (Außenbeschickung)",
            notes: "Glatte Innen- oder Außenfläche je nach Maschinenkonzept",
          },
        ],
        caption: "Werte auf Basis standardmäßiger Fertigungsgrenzen für widerstandsgeschweißte Spaltsiebe.",
      },
      features: {
        heading: "Wesentliche Konstruktionsmerkmale",
        intro: "Ausgelegt für kontinuierliche Rotation, hohe Schlammmengen und abrasive Medien.",
        items: [
          {
            title: "Höchste Beulsteifigkeit",
            text: "Die wendelförmige Drahtwicklung bildet ein stabiles Gewölbe gegen äußere Pressdrücke.",
          },
          {
            title: "Glatte Feststoffförderung",
            text: "Feststoffe gleiten ungehindert entlang der Achse ohne Hängenbleiben an Querstegen.",
          },
          {
            title: "Stabile Längsträger",
            text: "Robuste axiale Tragstäbe verhindern Durchbiegung auch bei langen Spannweiten.",
          },
          {
            title: "Integrierte Einbauteile",
            text: "Vorbereitet für Laufringe, Antriebszahnkränze und interne Förderspiralen.",
          },
        ],
      },
      related: {
        heading: "Verwandte Spaltsiebzylinder",
        intro: "Vergleichen Sie Umfangsschlitz-Zylinder mit axialer Schlitzausrichtung.",
        cards: [
          {
            title: "Axialschlitz-Zylinder",
            text: "Kontinuierliche Schlitze verlaufen achsenparallel und bilden saubere Führungsbahnen für mechanische Abstreifer, Rückspülfilter und Radialdurchströmung.",
            to: "/AxialSlotCylindersPage",
            badge: "Achsenparallel",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
              alt: "Axialschlitz-Spaltsiebzylinder mit achsenparallelem Profildraht",
            },
            linkLabel: "Zu den Axialschlitz-Zylindern",
          },
          {
            title: "Zylindrische Spaltsiebe Übersicht",
            text: "Gesamte Übersicht zu Bauformen, Fertigungsgrenzen und Einsatzbereichen zylindrischer Spaltsiebe.",
            to: "/CylindricalScreensPage",
            badge: "Hauptkategorie",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Industrielle zylindrische Edelstahl-Spaltsiebe und Filterstützkörbe",
            },
            linkLabel: "Zur Zylinder-Übersicht",
          },
        ],
      },
      cta: {
        id: "circumferential-slot-quote",
        eyebrow: "Technische Anfrage",
        heading: "Angebot für Umfangsschlitz-Zylinder anfordern",
        lead: "Senden Sie uns Trommeldurchmesser, Länge, Spaltweite und Einbaudetails für ein detailliertes Angebot.",
        secondaryLabel: "Zurück zu Zylindrische Spaltsiebe",
        secondaryTo: "/CylindricalScreensPage",
        note: "Werksprüfzeugnisse und PMI-Prüfungen auf Anforderung im Auftrag lieferbar.",
      },
    },
  },
  fr: {
    diagramCopy: {
      sectionTitle: "Architecture Rotative à Fentes Circonférentielles",
      sectionLead: "Les fils profilés enroulés à 360 degrés soudés sur des supports longitudinaux offrent une résistance exceptionnelle à l'écrasement radial pour trommels et tambours.",
    },
    content: {
      breadcrumbKey: "CircumferentialSlotCylinders",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Cylindres en Fil Profilé",
        standardBadge: "Fil en V Hélicoïdal",
        heading: "Cylindres à Fente Circonférentielle",
        lead: "Cylindres filtrants renforcés avec fils profilés continus enroulés autour de la circonférence. Conçus pour les trommels rotatifs, tambours d'égouttage et séparateurs à alimentation interne exigeant une grande rigidité circonférentielle et un transport axial régulier.",
        secondaryCta: {
          label: "Voir Tous les Cylindres",
          to: "/CylindricalScreensPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
          alt: "Cylindre en fil profilé à fente circonférentielle pour trommel rotatif",
        },
        cardNote: "Tambours Rotatifs et Trommels · 360°",
        cardLink: {
          label: "Demander un Devis",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Largeur de fente", value: "0.02 – 5.0 mm" },
          { label: "Orientation", value: "Circonférentielle" },
          { label: "Plage de diamètres", value: "25.4 – 1200 mm" },
          { label: "Alliages standards", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Résistance Radiale et Transport Axial des Matières",
        heading: "Enroulement Hélicoïdal Continu pour Trommels et Égoutteurs",
        paragraphs: [
          "Les cylindres à fentes circonférentielles MutualFilter intègrent un fil en V laminé enroulé en spirale autour d'une armature de tiges axiales. Le soudage par résistance à chaque intersection forme une virole rigide à haute résistance circonférentielle, capable de supporter les efforts centrifuges et le poids des boues denses.",
          "Dans un trommel rotatif, les solides roulent ou glissent longitudinalement tandis que l'eau s'évacue radialement à travers les fentes circonférentielles. Les matières reposent sur plusieurs fils adjacents, ce qui évite que des éléments allongés ne se coincent dans les fentes.",
          "Proposés avec surface utile lisse à l'intérieur (FITO) pour les tambours d'alimentation interne équipés de spires de convoyage, ou à l'extérieur (FOTI) pour l'égouttage à alimentation externe et les filtres à cartouche.",
        ],
        keyPoints: [
          "L'enroulement hélicoïdal continu à 360 degrés procure une résistance remarquable à l'affaissement radial.",
          "La disposition circonférentielle permet aux solides de progresser axialement sans coincement dans les fentes.",
          "Disponible avec face utile lisse intérieure pour tambours rotatifs ou face lisse extérieure pour filtres radiaux.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
          alt: "Détail de cylindre à fentes circonférentielles et tiges longitudinales",
        },
      },
      specTable: {
        heading: "Données Techniques des Cylindres Circonférentiels",
        intro: "Fabrication d'éléments jusqu'à 1200 mm de diamètre avec bandages de roulement, brides et raidisseurs.",
        columns: [
          { key: "parameter", label: "Caractéristique", align: "left" },
          { key: "specification", label: "Spécification", align: "left" },
          { key: "notes", label: "Notes de Conception", align: "left" },
        ],
        rows: [
          {
            parameter: "Largeur de fente",
            specification: "0.02 – 5.0 mm",
            notes: "Écartement rigoureusement constant sur tout le pourtour et la longueur",
          },
          {
            parameter: "Orientation",
            specification: "Circonférentielle (Radiale)",
            notes: "Fil continu hélicoïdal sur tiges axiales de structure",
          },
          {
            parameter: "Diamètres",
            specification: "25.4 – 1200 mm",
            notes: "Du petit panier filtrant aux tambours rotatifs industriels de grande taille",
          },
          {
            parameter: "Alliages standards",
            specification: "AISI 304, AISI 316, 316L",
            notes: "Inox austénitique résistant à la corrosion et à l'usure abrasive",
          },
          {
            parameter: "Sens du flux",
            specification: "FITO (Alimentation interne) ou FOTI (Alimentation externe)",
            notes: "Face lisse intérieure pour trommel, extérieure pour panier de filtration",
          },
        ],
        caption: "Données selon limites de fabrication standards du fil profilé soudé par résistance.",
      },
      features: {
        heading: "Capacités Techniques Notables",
        intro: "Façonnés pour résister à la rotation dynamique continue, aux charges lourdes et à l'abrasion.",
        items: [
          {
            title: "Excellente Tenue Mécanique",
            text: "L'enroulement hélicoïdal continu forme une voûte géométrique résistante aux pressions externes.",
          },
          {
            title: "Progression Axiale Fluide",
            text: "Les matières glissent sans retenue sur toute la longueur sans buter contre des fils transversaux.",
          },
          {
            title: "Armature Axiale Robuste",
            text: "Tiges longitudinales soudées évitant tout fléchissement du cylindre sur les grandes longueurs.",
          },
          {
            title: "Équipements de Montage",
            text: "Possibilité d'intégrer chemins de roulement, couronnes dentées et spires de relevage internes.",
          },
        ],
      },
      related: {
        heading: "Cylindres Associés",
        intro: "Comparez la géométrie circonférentielle aux fentes axiales ou consultez la famille parente.",
        cards: [
          {
            title: "Cylindres à Fentes Axiales",
            text: "Les fentes continues sont parallèles à l'axe du cylindre, offrant une surface linéaire lisse pour les racleurs mécaniques et la séparation radiale à fort débit.",
            to: "/AxialSlotCylindersPage",
            badge: "Parallèle à l'axe",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
              alt: "Cylindre en fil profilé à fentes axiales et renforts longitudinaux",
            },
            linkLabel: "Voir les Cylindres Axiaux",
          },
          {
            title: "Cylindres Filtrants Général",
            text: "Consultez l'ensemble des gammes de cylindres en fil profilé, limites dimensionnelles et alliages.",
            to: "/CylindricalScreensPage",
            badge: "Catégorie Parente",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Tamis cylindriques industriels en fil profilé inox et éléments de renfort",
            },
            linkLabel: "Retour aux Cylindres Filtrants",
          },
        ],
      },
      cta: {
        id: "circumferential-slot-quote",
        eyebrow: "Étude et Devis",
        heading: "Demander une Offre pour Cylindre Circonférentiel",
        lead: "Indiquez diamètre, longueur, fente requise et configuration de montage pour une analyse technique.",
        secondaryLabel: "Retour aux Cylindres Filtrants",
        secondaryTo: "/CylindricalScreensPage",
        note: "Certificats matière et contrôle de nuance disponibles sur demande dans la commande.",
      },
    },
  },
  pt: {
    diagramCopy: {
      sectionTitle: "Arquitetura Rotativa de Fenda Circunferencial",
      sectionLead: "Arames enrolados em hélice contínua soldados a 360 graus sobre barras longitudinais garantem máxima resistência ao colapso radial em trommels e tambores rotativos.",
    },
    content: {
      breadcrumbKey: "CircumferentialSlotCylinders",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Cilindros de Arame em Cunha",
        standardBadge: "Arame em V Helicoidal",
        heading: "Cilindros de Fenda Circunferencial",
        lead: "Cilindros reforçados de arame em cunha com arames contínuos enrolados em espiral na circunferência. Projetados para trommels rotativos, peneiras de desaguamento e tambores de alimentação interna que exigem alta resistência radial e fluxo axial contínuo.",
        secondaryCta: {
          label: "Ver Todos os Cilindros",
          to: "/CylindricalScreensPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
          alt: "Cilindro de arame em cunha com ranhura circunferencial para trommel rotativo",
        },
        cardNote: "Tambores Rotativos e Trommels · 360°",
        cardLink: {
          label: "Solicitar Cotação",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Abertura da fenda", value: "0.02 – 5.0 mm" },
          { label: "Orientação", value: "Circunferencial" },
          { label: "Faixa de diâmetro", value: "25.4 – 1200 mm" },
          { label: "Ligas padrão", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Resistência Radial e Transporte Axial Rotativo",
        heading: "Enrolamento Helicoidal Contínuo para Tambores Rotativos e Desaguamento",
        paragraphs: [
          "Os cilindros de fenda circunferencial MutualFilter apresentam arames em V enrolados em espiral contínua sobre barras longitudinais de sustentação. A soldagem por resistência em cada cruzamento cria uma estrutura tubular com excepcional resistência ao colapso, capaz de suportar forças centrífugas e cargas pesadas de polpas.",
          "Em tambores rotativos, os sólidos tombam e avançam axialmente ao longo do cilindro, enquanto o líquido drena radialmente pelas fendas. Como os arames circundam o perímetro, as partículas se apoiam sobre arames adjacentes, impedindo que materiais pontiagudos se encavelem nas fendas.",
          "Fabricados com face de trabalho lisa interna (FITO) para tambores de alimentação interna com hélices helicoidais de avanço, ou externa (FOTI) para tambores de alimentação externa e elementos de cartucho.",
        ],
        keyPoints: [
          "O enrolamento contínuo em 360 graus confere rigidez superior contra colapso sob pressão radial externa.",
          "A orientação circunferencial das fendas permite que sólidos avancem suavemente sem enroscar nas aberturas.",
          "Disponível com face útil interna lisa para trommels rotativos ou face externa lisa para cestos e filtros industriais.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
          alt: "Detalhe de cilindro de fenda circunferencial e barras longitudinais",
        },
      },
      specTable: {
        heading: "Dados Técnicos dos Cilindros Circunferenciais",
        intro: "Fabricação customizada com diâmetros de até 1200 mm, anéis de rolamento, flanges e anéis de reforço.",
        columns: [
          { key: "parameter", label: "Parâmetro", align: "left" },
          { key: "specification", label: "Especificação Padrão", align: "left" },
          { key: "notes", label: "Notas de Engenharia", align: "left" },
        ],
        rows: [
          {
            parameter: "Abertura da fenda",
            specification: "0.02 – 5.0 mm",
            notes: "Espaçamento preciso e uniforme em toda a circunferência e comprimento",
          },
          {
            parameter: "Orientação da fenda",
            specification: "Circunferencial (Radial)",
            notes: "Arame em V contínuo enrolado em espiral sobre barras axiais",
          },
          {
            parameter: "Faixa de diâmetro",
            specification: "25.4 – 1200 mm",
            notes: "De elementos filtrantes tubulares a cilindros trommel industriais de grande porte",
          },
          {
            parameter: "Ligas padrão",
            specification: "AISI 304, AISI 316, 316L",
            notes: "Aço inoxidável austenítico com resistência química e mecânica",
          },
          {
            parameter: "Sentido do fluxo",
            specification: "FITO (Alimentação interna) ou FOTI (Alimentação externa)",
            notes: "Superfície lisa interna para tambores rotativos ou externa para filtros",
          },
        ],
        caption: "Dados técnicos baseados nas capacidades de solda por resistência para fenda circunferencial.",
      },
      features: {
        heading: "Destaques Construtivos",
        intro: "Projetados para rotação dinâmica contínua, cargas elevadas e fluidos abrasivos.",
        items: [
          {
            title: "Alta Rigidez ao Colapso",
            text: "O enrolamento helicoidal contínuo forma um arco estrutural resistente a pressões externas.",
          },
          {
            title: "Avanço Axial dos Sólidos",
            text: "Os sólidos avançam sem atrito excessivo ao longo do cilindro sem bater em arames transversais.",
          },
          {
            title: "Barras Axiais Reforçadas",
            text: "Vigas longitudinais soldadas que impedem a flexão do tambor em comprimentos estendidos.",
          },
          {
            title: "Componentes Integrados",
            text: "Possibilidade de soldagem de anéis de rolamento, flanges e aletas internas helicoidais.",
          },
        ],
      },
      related: {
        heading: "Cilindros Filtrantes Relacionados",
        intro: "Compare a estrutura de fenda circunferencial com os cilindros de fenda axial.",
        cards: [
          {
            title: "Cilindros de Fenda Axial",
            text: "Fendas contínuas paralelas ao eixo do cilindro oferecem guias lineares limpas para sistemas de raspadores mecânicos e filtração radial.",
            to: "/AxialSlotCylindersPage",
            badge: "Paralela ao eixo",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
              alt: "Cilindro de arame em cunha com fenda axial e perfis longitudinais",
            },
            linkLabel: "Ver Cilindros Axiais",
          },
          {
            title: "Visão Geral de Cilindros Filtrantes",
            text: "Consulte todas as opções de cilindros de arame em cunha, faixas dimensionais e ligas metálicas.",
            to: "/CylindricalScreensPage",
            badge: "Categoria Principal",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Telas filtrantes cilíndricas industriais de arame em cunha e tubos de suporte",
            },
            linkLabel: "Voltar para Cilindros Filtrantes",
          },
        ],
      },
      cta: {
        id: "circumferential-slot-quote",
        eyebrow: "Consulta Técnica",
        heading: "Solicite Cotação para Cilindros Circunferenciais",
        lead: "Informe diâmetro, comprimento total, abertura de fenda e requisitos mecânicos à nossa engenharia.",
        secondaryLabel: "Voltar para Cilindros Filtrantes",
        secondaryTo: "/CylindricalScreensPage",
        note: "Certificados de ensaio de material e identificação positiva disponíveis sob pedido no pedido.",
      },
    },
  },
};
